'use client';

import { useCallback } from 'react';

/**
 * Custom hook for managing session storage data
 * @param storageKey - The key to use in sessionStorage (default: 'conveyancing-form-data')
 * @returns Object containing functions to get, save, and retrieve form data from sessionStorage
 */
export const useSessionStorage = (storageKey: string = 'conveyancing-form-data') => {
  /**
   * Gets all form data from sessionStorage
   * @returns Record of step indices to form data objects
   */
  const getAllFormDataFromStorage = useCallback((): Record<number, Record<string, string>> => {
    if (typeof window === 'undefined') return {};
    try {
      const data = window.sessionStorage.getItem(storageKey);
      return data ? JSON.parse(data) : {};
    } catch (error) {
      console.error('Failed to get form data from sessionStorage:', error);
      return {};
    }
  }, [storageKey]);

  /**
   * Saves form data to sessionStorage for a specific step
   * @param stepIndex - The index of the step
   * @param formData - The form data to save
   */
  const saveFormDataToStorage = useCallback(
    (stepIndex: number, formData: Record<string, string>) => {
      if (typeof window === 'undefined') return;
      try {
        const allData = getAllFormDataFromStorage();
        allData[stepIndex] = formData;
        window.sessionStorage.setItem(storageKey, JSON.stringify(allData));
      } catch (error) {
        console.error('Failed to save form data to sessionStorage:', error);
      }
    },
    [storageKey, getAllFormDataFromStorage]
  );

  /**
   * Gets form data from sessionStorage for a specific step
   * @param stepIndex - The index of the step
   * @returns The form data for the step or null if not found
   */
  const getFormDataFromStorage = useCallback(
    (stepIndex: number): Record<string, string> | null => {
      if (typeof window === 'undefined') return null;
      try {
        const allData = getAllFormDataFromStorage();
        return allData[stepIndex] || null;
      } catch (error) {
        console.error('Failed to get form data from sessionStorage:', error);
        return null;
      }
    },
    [getAllFormDataFromStorage]
  );

  /**
   * Deletes form data from sessionStorage for a specific step
   * @param stepIndex - The index of the step to delete
   */
  const deleteFormDataFromStorage = useCallback(
    (stepIndex: number) => {
      if (typeof window === 'undefined') return;
      try {
        const allData = getAllFormDataFromStorage();
        delete allData[stepIndex];
        window.sessionStorage.setItem(storageKey, JSON.stringify(allData));
      } catch (error) {
        console.error('Failed to delete form data from sessionStorage:', error);
      }
    },
    [storageKey, getAllFormDataFromStorage]
  );

  /**
   * Clears all form data from sessionStorage for the storage key
   */
  const clearAllFormDataFromStorage = useCallback(() => {
    if (typeof window === 'undefined') return;
    try {
      window.sessionStorage.removeItem(storageKey);
    } catch (error) {
      console.error('Failed to clear form data from sessionStorage:', error);
    }
  }, [storageKey]);

  return {
    getAllFormDataFromStorage,
    saveFormDataToStorage,
    getFormDataFromStorage,
    deleteFormDataFromStorage,
    clearAllFormDataFromStorage,
  };
};
